import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient } from './query-client';
import { MEETINGS_SETUP_KEY } from './meetings-chats-empty';
import type { AlfredSettings } from './alfred-settings';
import type { AlfredSetupState } from './alfred-setup';
import { readShopifyConnectStatus } from './connector-card';
import { readComingSoon, type ComingSoonConnector } from './connector-requests';
import type { ShopifyHealth } from './shopify-health';

export async function fetchShopifyConnectStatus(attemptId: string) {
  const res = await apiFetch(`${API_BASE}/api/connectors/shopify-status?attemptId=${encodeURIComponent(attemptId)}`, { credentials: 'include' });
  if (!res.ok) throw new Error('shopify_connect_status_unavailable');
  const status = readShopifyConnectStatus(await res.json());
  if (!status) throw new Error('invalid_shopify_connect_status');
  return status;
}
// Client-side data layer for Connectors (third-party Composio integrations:
// Gmail, Google Calendar, Outlook, Slack, …). Mirrors lib/widgets.ts: hit
// `${API_BASE}/api/connectors/*` with credentials. The API routes resolve the
// caller's metasite from their identity.

/** Backing system for a connector row. Omitted/`composio` = a dynamic Composio connector.
 *  `native` = a calendar row; `mcp` = an McpConnection-backed first-party connector;
 *  `wix-service` = a connector whose lifecycle belongs to another Wix service. */
export type ConnectorProvider = 'composio' | 'native' | 'mcp' | 'wix-service';

/** Overrides where a row's Connect goes when the provider alone doesn't decide it. `assistant-chat` means
 *  the connect belongs in a conversation, because what decides whether it succeeds has to be said to the
 *  user before they leave — a partner install's paid partner plan, today. Optional: absent means the
 *  provider's ordinary route. Mirrors the server's `ConnectorConnectVia` and native's `connectVia`. */
export type ConnectorConnectVia = 'assistant-chat';

/** A connected account on a connector row — a Composio connected-account id, or a native calendar token
 *  id. `label` is its email/handle when known (else the client shows an ordinal fallback);
 *  `secondaryLabel` is the optional human/workspace line beneath it ("Work, Pizza Milano" / "Personal").
 *  Mirrors native's `ConnectorAccount` — the field is server-optional and only some connectors carry it,
 *  so render it only when present. */
export interface ConnectorAccount {
  id: string;
  label?: string;
  secondaryLabel?: string;
}

export interface ConnectorStatus {
  toolkit: string;
  name?: string;
  logo?: string;
  connected: boolean;
  /** Backing system. Omitted = `composio` (the historical default). */
  provider?: ConnectorProvider;
  /** Overrides where this row's Connect goes. Omitted = the provider's ordinary route. */
  connectVia?: ConnectorConnectVia;
  /** Native calendar provider (`GOOGLE` | `MICROSOFT` | `ICLOUD`) for a `native` calendar row. */
  calendarProvider?: string;
  /** The Google row's connect asks for mail as well as calendar, so its card must say so. Decided
   *  server-side on a default-off key and carried on the row, so no client repeats the rule. */
  includesMail?: boolean;
  /** Connected native accounts (calendar token ids + emails) — what the client deletes on disconnect. */
  accounts?: ConnectorAccount[];
  /** Why a `connected` row cannot be used yet, in the vendor's words — the vendor's switches for this
   *  account are off. The credential is good, so the row stays connected; this is what the user acts on. */
  notice?: string;
  /** The provider has refused this row's stored authorization, so `connected` describes our bookkeeping
   *  rather than anything that works. Only the owner reconnecting restores it — a retry cannot. Kept
   *  separate from `notice`, whose credential is good. */
  needsReconnect?: boolean;
  shopifyHealth?: ShopifyHealth;
  /** Server-provided (Composio) English blurb. The client prefers a localized `connectors.desc.*` key
   *  (see `connectorDescriptionKey`) and only falls back to this. Mirrors native `connectorDescription`. */
  description?: string;
  /** Left out of the Connectors screen's tools list. Sent only as `true` (Alfred). */
  hiddenFromConnectorsList?: boolean;
  /** How many accounts the row may hold; absent means Composio's cap of 5. */
  maxAccounts?: number;
}

// The connected-row account-list rule lives in its own import-free module so it can be unit-tested; it is
// re-exported here so call sites keep importing it from `lib/connectors`. Mirrored 1:1 on iOS + Android.
export { canAddAccount, showsAccountList, supportsMultipleAccounts } from './connector-account-list';

/** Composio's hosted logo for a toolkit — same source the mobile rows use. */
export function connectorLogoUrl(toolkit: string): string {
  return `https://logos.composio.dev/api/${toolkit}`;
}

/** Curated label overrides; otherwise the server-provided name (or slug). Mirrors mobile's connector-row. */
const CONNECTOR_LABELS: Record<string, string> = {
  gmail: 'Gmail',
};

export function connectorDisplayName(connector: ConnectorStatus): string {
  return CONNECTOR_LABELS[connector.toolkit] ?? connector.name ?? connector.toolkit;
}

/** Localized-description i18n key for a KNOWN connector, else null (unknown toolkit → fall back to the
 *  server `description`, else hide the line). Mirrors native `ConnectorsScreen.descriptionKey`: native
 *  calendar rows map by `calendarProvider`; Composio rows use `connectors.desc.<toolkit-slug>`. */
export function connectorDescriptionKey(connector: ConnectorStatus): string | null {
  if (connector.provider === 'native') {
    switch ((connector.calendarProvider ?? '').toUpperCase()) {
      case 'GOOGLE':
        // A card that promises only availability and events, over a consent screen asking to send mail,
        // is the mismatch that makes someone refuse the grant.
        return connector.includesMail ? 'connectors.desc.google.withMail' : 'connectors.desc.calendar.google';
      case 'MICROSOFT':
      case 'OUTLOOK':
        return 'connectors.desc.calendar.microsoft';
      case 'ICLOUD':
      case 'APPLE':
        return 'connectors.desc.calendar.apple';
      default:
        return null;
    }
  }
  const slug = connector.toolkit.toLowerCase();
  return slug ? `connectors.desc.${slug}` : null;
}

export interface ConnectorsStatus {
  /** False when the connectors feature isn't configured (no Composio key) — distinct from "none available". */
  enabled: boolean;
  connectors: ConnectorStatus[];
  /** Whether the multi-account rollout is on for this caller — gates the per-account UI + "Add another". */
  multiAccountEnabled: boolean;
  /** Whether a Google connect should also ask for the Gmail scopes. Decided server-side on a default-off
   *  databag key, and read from the status the screen already fetches — the consent popup has to open
   *  inside the user's click, so the answer has to be in hand before they click. */
  gmailScopesEnabled: boolean;
  /** Apps listed after the connectable ones that Symphony cannot connect yet; a tap asks Maestro. */
  comingSoon: ComingSoonConnector[];
}

export async function fetchConnectors(): Promise<ConnectorsStatus> {
  const res = await apiFetch(`${API_BASE}/api/connectors/status`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const data = (await res.json()) as {
    enabled?: boolean;
    connectors?: ConnectorStatus[];
    multiAccountEnabled?: boolean;
    gmailScopesEnabled?: boolean;
    comingSoon?: unknown;
  };
  return {
    enabled: data.enabled ?? false,
    connectors: data.connectors ?? [],
    multiAccountEnabled: data.multiAccountEnabled ?? false,
    // Absent ⇒ false: an older server, a failed read or a stripped field must never turn an unapproved
    // scope on. The cost of guessing wrong upward is a cap slot that cannot be reclaimed.
    gmailScopesEnabled: data.gmailScopesEnabled ?? false,
    comingSoon: readComingSoon(data.comingSoon),
  };
}

export interface AuthorizeResult {
  url: string | null;
  /** True when the toolkit already has an ACTIVE connection — refresh status instead of erroring. */
  alreadyConnected?: boolean;
  error?: string;
}

// `allowMultiple` is the explicit "add another account" intent — links an additional account on an
// already-connected toolkit (honored server-side only when the multi-account rollout is on).
export async function authorizeConnector(toolkit: string, allowMultiple = false): Promise<AuthorizeResult> {
  const query = `?toolkit=${encodeURIComponent(toolkit)}${allowMultiple ? '&allowMultiple=true' : ''}`;
  const res = await apiFetch(`${API_BASE}/api/connectors/authorize${query}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  return (await res.json()) as AuthorizeResult;
}

// `accountId` scopes the disconnect to ONE Composio account of a multiply-linked toolkit; omitted, the
// whole connector is disconnected (every account). Native calendar rows use `disconnectCalendarAccounts`.
// Resolves to the server's `disconnected` flag: a failed uninstall answers 200 with `disconnected: false`.
export async function disconnectConnector(toolkit: string, accountId?: string): Promise<boolean> {
  const query = `?toolkit=${encodeURIComponent(toolkit)}${accountId ? `&accountId=${encodeURIComponent(accountId)}` : ''}`;
  const res = await apiFetch(`${API_BASE}/api/connectors/disconnect${query}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const body = (await res.json().catch(() => null)) as { disconnected?: boolean } | null;
  return body?.disconnected !== false;
}

/**
 * Where Alfred's setup stands — the onboarding's read. Never buys anything. A refusal (`error`: off the
 * rollout, or Alfred did not answer) throws, so the modal shows its load failure rather than a state it was
 * never sent.
 */
export async function fetchAlfredSetup(): Promise<AlfredSetupState> {
  return alfredSetupFrom(await apiFetch(`${API_BASE}/api/connectors/alfred-setup`, { credentials: 'include' }));
}

/**
 * Runs Alfred's setup, which BUYS a phone number and turns on a receptionist charged per call. Called only
 * from the onboarding's "Get my number", the screen that says both.
 */
export async function runAlfredSetup(): Promise<AlfredSetupState> {
  return alfredSetupFrom(await apiFetch(`${API_BASE}/api/connectors/alfred-setup`, { method: 'POST', credentials: 'include' }));
}

async function alfredSetupFrom(res: Response): Promise<AlfredSetupState> {
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const data = (await res.json()) as { setup?: AlfredSetupState; error?: string };
  if (!data.setup) {
    throw new Error(data.error ?? 'alfred_unavailable');
  }
  return data.setup;
}

/**
 * Alfred as its callers meet it — the settings screen's read. Never buys or changes anything. `null` means
 * Alfred is not installed on the site. A refusal throws with the server's reason (`not_available` off either
 * gate, `alfred_unavailable` when Alfred did not answer), so the page can tell "send me back to the row" from
 * "try again".
 */
export async function fetchAlfredSettings(): Promise<AlfredSettings | null> {
  const res = await apiFetch(`${API_BASE}/api/connectors/alfred-settings`, { credentials: 'include' });
  const data = (await res.json().catch(() => ({}))) as { settings?: AlfredSettings | null; error?: string };
  if (!res.ok || data.error || data.settings === undefined) {
    throw new Error(data.error ?? 'alfred_unavailable');
  }
  return data.settings;
}

/**
 * Disconnect a NATIVE calendar row by deleting its backing calendar token(s) — the same `/api/calendars`
 * DELETE the settings Calendars section uses. A native row carries one token id per connected account.
 */
export async function disconnectCalendarAccounts(accountIds: string[]): Promise<void> {
  for (const id of accountIds) {
    const res = await apiFetch(`${API_BASE}/api/calendars?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      throw new Error(await readErrorMessage(res));
    }
  }
  void queryClient.invalidateQueries({ queryKey: MEETINGS_SETUP_KEY });
}

/** Map the server's string `calendarProvider` to the web calendar catalog's ProviderId. */
export function calendarProviderToProviderId(calendarProvider?: string): 'google' | 'microsoft' | 'apple' | null {
  return calendarProvider === 'GOOGLE' ? 'google' : calendarProvider === 'MICROSOFT' ? 'microsoft' : calendarProvider === 'ICLOUD' ? 'apple' : null;
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; message?: string };
    return data.error || data.message || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
